import type { Release } from '@src/dto';
import { Download } from 'lucide-react';
import { formatFileSize, formatShortDate } from '../../../utils/stringHelpers';
import { CopyableValue } from '../../common/detailParts';
import { PortalCard } from '../../portal';
import { DetailDisclosure, DetailLabel } from './releaseDetailParts';

interface BuildSectionProps {
  release: Release;
  apkDownloadUrl: string | null;
  arweaveApkUrl: string | null;
}

export function BuildSection({
  release,
  apkDownloadUrl,
  arweaveApkUrl,
}: BuildSectionProps) {
  const downloadUrl = apkDownloadUrl || arweaveApkUrl;
  const showAndroidInfo =
    release.status === 'Processed' || release.status === 'Published';
  const fingerprints: Array<{ label: string; value: string }> = [];
  if (release.releaseFileHash) {
    fingerprints.push({ label: 'APK SHA-256', value: release.releaseFileHash });
  }
  if (release.certificateFingerprint) {
    fingerprints.push({
      label: 'Certificate SHA-256',
      value: release.certificateFingerprint,
    });
  }
  const submittedDate =
    release.publishedAt ?? release.submittedAt ?? release.created;

  return (
    <PortalCard className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-white">
          <span>Submitted {formatShortDate(submittedDate) || '-'}</span>
          {release.versionCode != null && (
            <span className="tabular-nums">v{release.versionCode}</span>
          )}
          {release.hubspotTicketId && (
            <span className="font-mono">#{release.hubspotTicketId}</span>
          )}
        </div>
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-seeker-700 px-4 text-sm font-bold text-white transition-colors duration-150 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900 sm:w-auto"
          >
            <Download className="size-4" />
            Download APK
          </a>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4 border-b border-seeker-800 pb-5">
        <div className="min-w-0">
          <div
            className="truncate font-mono text-base text-white"
            title={release.releaseFileName}
          >
            {release.releaseFileName}
          </div>
          <div className="mt-0.5 text-xs text-seeker-400">
            Uploaded {formatShortDate(release.created) || '-'}
          </div>
        </div>
        <div className="text-base font-medium tabular-nums text-white">
          {formatFileSize(release.releaseFileSize)}
        </div>
      </div>

      <div className="mt-5 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        {showAndroidInfo && release.androidPackage && (
          <div className="flex min-w-0 flex-col gap-1">
            <DetailLabel>Package</DetailLabel>
            <span
              className="truncate font-mono text-base text-white"
              title={release.androidPackage}
            >
              {release.androidPackage}
            </span>
          </div>
        )}
        {showAndroidInfo && (
          <div className="flex flex-col gap-1">
            <DetailLabel>SDK</DetailLabel>
            <span className="font-mono text-base tabular-nums text-white">
              {release.minSdkVersion ?? '—'}
              <span className="mx-2 text-seeker-400">→</span>
              {release.targetSdkVersion ?? '—'}
            </span>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <DetailLabel>Version</DetailLabel>
          <span className="font-mono text-base tabular-nums text-white">
            {release.versionName || '—'}
            {release.versionCode != null && (
              <span className="ml-2 text-seeker-400">
                ({release.versionCode})
              </span>
            )}
          </span>
        </div>
        {showAndroidInfo && release.locales?.length > 0 && (
          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-3">
            <DetailLabel>Languages</DetailLabel>
            <div className="flex flex-wrap gap-2">
              {release.locales.map((locale, index) => (
                <span
                  key={`${locale}-${index}`}
                  className="rounded-md bg-seeker-800 px-2 py-1 font-mono text-xs text-white"
                >
                  {locale}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {fingerprints.length > 0 && (
        <DetailDisclosure label="Fingerprints" count={fingerprints.length}>
          <div className="flex flex-col gap-4">
            {fingerprints.map((entry) => (
              <div key={entry.label} className="flex flex-col gap-1">
                <DetailLabel>{entry.label}</DetailLabel>
                <CopyableValue
                  value={entry.value}
                  truncate={false}
                  className="text-xs"
                />
              </div>
            ))}
          </div>
        </DetailDisclosure>
      )}
    </PortalCard>
  );
}
